// Analytics Cup edition pages — shared by 1.0 and 2.0.
//
// Sections and their copy come from the edition JSON; this file only decides how
// each `type` renders. Styling lives in Edition.scss under
// `.analytics-cup--edition`.

import React, { useState, useEffect, useMemo, useRef } from 'react';
import { useParams, useNavigate, Navigate, Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import { AnalyticsCupNav } from './components/AnalyticsCupNav';
import './AnalyticsCup.scss';
import './Edition.scss';

import edition1 from '../../assets/analytics_cup/editions/analytics-cup.json';
import edition2 from '../../assets/analytics_cup/editions/analytics-cup2.json';
import submissionsCSVFile from '../../assets/analytics_cup/analytics-cup-26.csv';
import { findVideo, youtubeUrl } from '../../assets/analytics_cup/videoLookup';

const EDITIONS = {
    'analytics-cup': edition1,
    'analytics-cup2': edition2,
};

// ── CSV helpers ───────────────────────────────────────────────────────────────

const parseCSVLine = (line) => {
    const result = [];
    let current = '';
    let inQuotes = false;
    for (let i = 0; i < line.length; i++) {
        const ch = line[i];
        if (ch === '"') { inQuotes = !inQuotes; }
        else if (ch === ',' && !inQuotes) { result.push(current.trim()); current = ''; }
        else { current += ch; }
    }
    result.push(current.trim());
    return result;
};

const parseCSV = (text) =>
    text.trim().split('\n').slice(1).map(line => {
        const v = parseCSVLine(line);
        return {
            id: v[0], title: v[1], track: v[2], name: v[3],
            finalist: v[4] === 'TRUE',
            honourableMention: v[5] === 'TRUE',
            dataUsed: v[6], github: v[7],
        };
    });

const capitalizeName = (name = '') =>
    name.toLowerCase().split(' ')
        .map(w => w.charAt(0).toUpperCase() + w.slice(1))
        .join(' ').replace(/_/g, ' ');

const useSubmissions = (csvFile) => {
    const [submissions, setSubmissions] = useState([]);
    useEffect(() => {
        if (!csvFile) return;
        fetch(submissionsCSVFile)
            .then(r => r.text())
            .then(text => setSubmissions(parseCSV(text)))
            .catch(() => {});
    }, [csvFile]);
    return submissions;
};


// ── Shared primitives ─────────────────────────────────────────────────────────

const Paragraphs = ({ paragraphs = [] }) => (
    <>
        {paragraphs.map((p, i) => (
            <p key={i} dangerouslySetInnerHTML={{ __html: p }} />
        ))}
    </>
);

// The custom bullet (square, top-left corner sharp) is drawn in SCSS via ::before.
const Bullets = ({ items = [] }) => (
    <ul className="acp-bullets">
        {items.map((item, i) => (
            <li key={i} dangerouslySetInnerHTML={{ __html: item }} />
        ))}
    </ul>
);

const SPORT_ICONS = {
    football: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
            <circle cx="12" cy="12" r="10" />
            <path d="M12 7l4 3-1.5 4.5h-5L8 10z" />
            <path d="M12 2v5M2.5 9l5.5 1M21.5 9L16 10M6 20l2.5-5.5M18 20l-2.5-5.5" />
        </svg>
    ),
    basketball: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" aria-hidden="true">
            <circle cx="12" cy="12" r="10" />
            <path d="M12 2v20M2 12h20" />
            <path d="M5 5c4 3 4 11 0 14M19 5c-4 3-4 11 0 14" />
        </svg>
    ),
};

// Off-site links open in a new tab; internal routes stay in-tab.
const linkProps = (url = '') =>
    /^https?:\/\//i.test(url) ? { target: '_blank', rel: 'noopener noreferrer' } : {};

const iconForTitle = (title = '') => {
    const t = title.toLowerCase();
    if (t.includes('football')) return SPORT_ICONS.football;
    if (t.includes('basketball')) return SPORT_ICONS.basketball;
    return null;
};

const REGION_FLAGS = {
    europe: '🇪🇺',
    us: '🇺🇸',
};

const flagForTitle = (title = '') => {
    const t = title.toLowerCase();
    if (t.includes('europe')) return REGION_FLAGS.europe;
    if (t.includes('us')) return REGION_FLAGS.us;
    return null;
};

// ── Renderers ─────────────────────────────────────────────────────────────────

// `mobileOnly` — true for the one image kept below the mobile breakpoint,
// false for the ones hidden there, undefined when the distinction doesn't apply.
const Figure = ({ src, caption, mobileOnly }) => {
    if (!src) return null;
    const modifier = mobileOnly === undefined ? ''
        : mobileOnly ? ' acp-content-image__media--keep-mobile'
            : ' acp-content-image__media--hide-mobile';
    return (
        <figure className={`acp-content-image__media${modifier}`}>
            <img src={src} alt={caption || ''} loading="lazy" />
            {caption && <figcaption className="acp-content-image__caption">{caption}</figcaption>}
        </figure>
    );
};

// Puts a block beside its artwork using the same grid as the content-image
// sections, so it inherits the half-width cap and top alignment instead of
// growing its own layout. Without artwork the block renders full width.
//
// `images` stacks several; `mobileImage` names the one that survives the mobile
// breakpoint, where a stack would be too tall.
const WithFigure = ({ data, children }) => {
    const stack = data.images || (data.image ? [data.image] : []);
    if (!stack.length) return children;

    return (
        <div className="acp-content-image">
            <div className="acp-content-image__text">{children}</div>
            {stack.length === 1 ? (
                <Figure src={stack[0]} caption={data.caption} />
            ) : (
                <div className="acp-figure-stack">
                    {stack.map(src => (
                        <Figure
                            key={src}
                            src={src}
                            mobileOnly={data.mobileImage ? src === data.mobileImage : undefined}
                        />
                    ))}
                    {data.caption && (
                        <figcaption className="acp-content-image__caption">{data.caption}</figcaption>
                    )}
                </div>
            )}
        </div>
    );
};

const GitHubIcon = () => (
    <svg width="18" height="18" viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M12 0c-6.626 0-12 5.373-12 12 0 5.302 3.438 9.8 8.207 11.387.599.111.793-.261.793-.577v-2.234c-3.338.726-4.033-1.416-4.033-1.416-.546-1.387-1.333-1.756-1.333-1.756-1.089-.745.083-.729.083-.729 1.205.084 1.839 1.237 1.839 1.237 1.07 1.834 2.807 1.304 3.492.997.107-.775.418-1.305.762-1.604-2.665-.305-5.467-1.334-5.467-5.931 0-1.311.469-2.381 1.236-3.221-.124-.303-.535-1.524.117-3.176 0 0 1.008-.322 3.301 1.23.957-.266 1.983-.399 3.003-.404 1.02.005 2.047.138 3.006.404 2.291-1.552 3.297-1.23 3.297-1.23.653 1.653.242 2.874.118 3.176.77.84 1.235 1.911 1.235 3.221 0 4.609-2.807 5.624-5.479 5.921.43.372.823 1.102.823 2.222v3.293c0 .319.192.694.801.576 4.765-1.589 8.199-6.086 8.199-11.386 0-6.627-5.373-12-12-12z" />
    </svg>
);

const ContentImage = ({ data }) => (
    <div className={`acp-content-image${data.stacked ? ' acp-content-image--stacked' : ''}`}>
        <div className="acp-content-image__text">
            {data.heading && <h3 className="acp-subsection__title">{data.heading}</h3>}
            {data.subheading && data.subheading !== data.title && (
                <p className="acp-content-image__subheading">{data.subheading}</p>
            )}
            <Paragraphs paragraphs={data.paragraphs} />

            {data.ctas && (
                <div className="acp-content-image__ctas">
                    {data.ctas.map((cta, i) => cta.disabled ? (
                        <button key={i} type="button" disabled className="acp-btn-outline is-disabled">
                            {cta.label}
                        </button>
                    ) : (
                        <a key={i} href={cta.url} className="acp-btn-outline" {...linkProps(cta.url)}>
                            {cta.icon === 'github' && <GitHubIcon />}
                            {cta.label}
                        </a>
                    ))}
                </div>
            )}

            {data.link && (
                <a href={data.link.url} className="acp-btn-outline" {...linkProps(data.link.url)}>
                    {data.link.label}
                </a>
            )}
        </div>
        <Figure src={data.image} caption={data.caption} />
    </div>
);

const BulletsBold = ({ data }) => (
    <ul className="acp-glance">
        {(data.items || []).map((item, i) => (
            <li key={i}>
                <span className="acp-glance__label">{item.label}</span>
                <span className="acp-glance__detail"
                    dangerouslySetInnerHTML={{ __html: item.detail }} />
            </li>
        ))}
    </ul>
);

// `two-column` covers two distinct card families:
//   columns[].body  → Challenge cards (Football / Basketball themes)
//   columns[].items → Edition cards   (Europe / US regional competitions)
const TwoColumn = ({ data }) => {
    const isEdition = (data.columns || []).some(c => c.items);
    const kind = isEdition ? 'edition' : 'challenge';

    return (
        <div className="acp-two-col">
            {data.intro && (
                <div className="acp-two-col__intro">
                    {data.intro.split('\n\n').map((p, i) => <p key={i}>{p}</p>)}
                </div>
            )}
            <div className={`acp-card-grid acp-card-grid--${kind}`}>
                {(data.columns || []).map((col, i) => {
                    const icon = isEdition ? null : iconForTitle(col.title);
                    const flag = isEdition ? flagForTitle(col.title) : null;
                    return (
                        <article key={i} className={`acp-card acp-card--${kind}`}>
                            <header className="acp-card__head">
                                {icon && <span className="acp-card__icon">{icon}</span>}
                                {flag && <span className="acp-card__flag">{flag}</span>}
                                <h4 className="acp-card__title">{col.title}</h4>
                            </header>
                            <div className="acp-card__body">
                                {col.items && <Bullets items={col.items} />}
                                {col.question && (
                                    <p className="acp-card__question">{col.question}</p>
                                )}
                                {col.paragraphs && <Paragraphs paragraphs={col.paragraphs} />}
                                {col.body && !col.question && <p>{col.body}</p>}
                            </div>
                        </article>
                    );
                })}
            </div>
        </div>
    );
};

const DatasetCard = ({ data }) => (
    <article className="acp-card acp-card--dataset">
        <header className="acp-card__head">
            {/* Edition 1's dataset isn't a sport name, so there may be no icon —
                the tile is a filled cyan square and would render empty. */}
            {iconForTitle(data.name) && (
                <span className="acp-card__icon">{iconForTitle(data.name)}</span>
            )}
            <h4 className="acp-card__title">{data.name}</h4>
        </header>
        <div className="acp-card__body">
            {data.paragraphs && <Paragraphs paragraphs={data.paragraphs} />}
            {data.items && (
                <>
                    <p className="acp-card__list-label">Includes:</p>
                    <Bullets items={data.items} />
                </>
            )}
            {(data.cta || data.link) && (
                <a href={(data.cta || data.link).url} className="acp-btn-outline"
                    {...linkProps((data.cta || data.link).url)}>
                    {(data.cta || data.link).label}
                </a>
            )}
        </div>
    </article>
);

const TIMELINE_ICONS = {
    flag: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <path d="M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1z" />
            <line x1="4" y1="22" x2="4" y2="15" />
        </svg>
    ),
    clock: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <circle cx="12" cy="12" r="10" />
            <polyline points="12 6 12 12 16 14" />
        </svg>
    ),
    star: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <polygon points="12 2 15.09 8.26 22 9.27 17 14.14 18.18 21.02 12 17.77 5.82 21.02 7 14.14 2 9.27 8.91 8.26 12 2" />
        </svg>
    ),
    trophy: (
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
            <polyline points="8 21 12 21 16 21" />
            <line x1="12" y1="17" x2="12" y2="21" />
            <path d="M7 4h10v7a5 5 0 0 1-10 0V4z" />
            <path d="M17 6h2a2 2 0 0 1 2 2v1a4 4 0 0 0-4 5" />
            <path d="M7 6H5a2 2 0 0 0-2 2v1a4 4 0 0 0 4 5" />
        </svg>
    ),
};

const Timeline = ({ data }) => (
    <WithFigure data={data}>
        <ol className="acp-timeline">
            {(data.dates || []).map((d, i) => (
                <li key={i} className="acp-timeline__item">
                    <div className="acp-timeline__marker">
                        <span className="acp-timeline__icon">{TIMELINE_ICONS[d.icon]}</span>
                    </div>
                    <div className="acp-timeline__body">
                        <span className="acp-timeline__date">{d.date}</span>
                        <span className="acp-timeline__label">{d.label}</span>
                    </div>
                </li>
            ))}
        </ol>
    </WithFigure>
);

// Prize tile icons, keyed by semantic slot so the copy can change without
// touching the artwork.
const svg = (children) => (
    <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.75"
        strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
        {children}
    </svg>
);

const PRIZE_ICONS = {
        finals: svg(<>
            <rect x="3" y="4" width="18" height="12" rx="1" />
            <path d="M12 16v4M8 20h8" />
            <path d="M7 12l3-3 2 2 4-4" />
        </>),
        award: svg(<>
            <rect x="2" y="6" width="20" height="12" rx="2" />
            <circle cx="12" cy="12" r="2.5" />
            <path d="M5.5 12h.01M18.5 12h.01" />
        </>),
        grant: svg(<>
            <circle cx="6" cy="6" r="2.5" />
            <circle cx="6" cy="18" r="2.5" />
            <circle cx="18" cy="18" r="2.5" />
            <path d="M6 8.5v7" />
            <path d="M18 15.5V10a3 3 0 0 0-3-3h-3" />
            <path d="M14.5 4.5L12 7l2.5 2.5" />
        </>),
    };

const Prizes = ({ data }) => {
    const items = data.items || [];

    // Notes are pulled out of the cards and run as numbered footnotes under the
    // grid, with a matching superscript on the card they belong to.
    const noted = items.filter(item => item.note);
    const markerFor = (item) => {
        const idx = noted.indexOf(item);
        return idx === -1 ? null : String(idx + 1);
    };

    return (
        <div className="acp-prizes">
            {data.intro && <p className="acp-prizes__intro">{data.intro}</p>}
            <div className="acp-prizes__grid">
                {items.map((item, i) => {
                    const marker = markerFor(item);
                    return (
                        <article key={i} className="acp-card acp-card--prize">
                            <header className="acp-card__head">
                                {/* Edition 2 names a slot; edition 1 stores the
                                    emoji itself. */}
                                {PRIZE_ICONS[item.icon] ? (
                                    <span className="acp-card__icon">{PRIZE_ICONS[item.icon]}</span>
                                ) : item.icon ? (
                                    <span className="acp-card__emoji">{item.icon}</span>
                                ) : null}
                                <h4 className="acp-card__title">{item.title}</h4>
                            </header>
                            <div className="acp-card__body">
                                <p>
                                    <span dangerouslySetInnerHTML={{ __html: item.detail }} />
                                    {marker && <sup className="acp-card__ref">{marker}</sup>}
                                </p>
                            </div>
                        </article>
                    );
                })}
            </div>
            {noted.length > 0 && (
                <p className="acp-prizes__footnotes">
                    {noted.map((item, i) => (
                        <span key={i} className="acp-prizes__footnote">
                            <span className="acp-prizes__ref">{i + 1}</span>
                            {item.note}
                        </span>
                    ))}
                </p>
            )}
        </div>
    );
};

const Judging = ({ data }) => {
    const criteria = (
        <div className="acp-judging">
            {(data.criteria || []).map((c, i) => (
                <div key={i} className="acp-judging__item">
                    <span className="acp-judging__weight">{c.weight}</span>
                    <div>
                        <strong>{c.label}</strong>
                        <span className="acp-judging__detail"> - {c.detail}</span>
                    </div>
                </div>
            ))}
        </div>
    );

    return <WithFigure data={data}>{criteria}</WithFigure>;
};

// A short fragment before a colon reads as a label and is bolded; a colon deep
// in a sentence is left alone.
const LABEL_LIMIT = 40;

const StepText = ({ text }) => {
    const colon = text.indexOf(':');
    if (colon > 0 && colon < LABEL_LIMIT) {
        return (
            <>
                <strong dangerouslySetInnerHTML={{ __html: text.slice(0, colon + 1) }} />
                <span dangerouslySetInnerHTML={{ __html: text.slice(colon + 1) }} />
            </>
        );
    }
    return <span dangerouslySetInnerHTML={{ __html: text }} />;
};

const Steps = ({ data }) => (
    <>
    <ol className="acp-steps">
        {(data.items || []).map((step, i) => {
            // An item is either a string or { text, items } for a nested list.
            const { text, items } = typeof step === 'string' ? { text: step } : step;
            return (
                <li key={i}>
                    <span className="acp-steps__num">{i + 1}</span>
                    <span className="acp-steps__text">
                        <StepText text={text} />
                        {items && (
                            <ol className="acp-steps__sub">
                                {items.map((sub, j) => (
                                    <li key={j} dangerouslySetInnerHTML={{ __html: sub }} />
                                ))}
                            </ol>
                        )}
                    </span>
                </li>
            );
        })}
    </ol>

    {/* Edition 1 attaches submission template repos to its How to Enter steps. */}
    {data.templates && (
        <div className="acp-steps__templates">
            <p className="acp-steps__templates-label"><strong>Submission templates:</strong></p>
            <div className="acp-steps__template-links">
                {data.templates.map((t, i) => (
                    <a key={i} href={t.url} className="acp-btn-outline" {...linkProps(t.url)}>
                        {t.label}
                    </a>
                ))}
            </div>
        </div>
    )}
    </>
);

const Content = ({ data }) => (
    <div className="acp-content">
        <Paragraphs paragraphs={data.paragraphs} />
    </div>
);

const FAQSegment = ({ data }) => {
    const [open, setOpen] = useState(null);
    return (
        <div className="acp-faq">
            {(data.items || []).map((item, i) => (
                <div key={i} className={`acp-faq__item${open === i ? ' is-open' : ''}`}>
                    <button
                        className="acp-faq__q"
                        aria-expanded={open === i}
                        onClick={() => setOpen(open === i ? null : i)}
                    >
                        <span>{item.question}</span>
                        <span className="acp-faq__toggle" aria-hidden="true" />
                    </button>
                    <div className="acp-faq__a">
                        <div dangerouslySetInnerHTML={{ __html: item.answer }} />
                    </div>
                </div>
            ))}
        </div>
    );
};

// One title per thing. A subsection heading is suppressed when it would restate
// something already on screen:
//   - it repeats the section heading verbatim
//   - it is the section's only subsection, so the section heading already names it
//   - the renderer prints the same string itself (dataset cards carry their name)
const rendersOwnTitle = (sub) =>
    (sub.type === 'dataset-card' && sub.name === sub.title) || Boolean(sub.heading);

// Sections built entirely from cards (the two datasets) sit side by side rather
// than stacking, so the pair reads as a comparison.
const isCardGrid = (section) => {
    const subs = section.subsections || [];
    return subs.length > 1 && subs.every(s => s.type === 'dataset-card');
};

const showSubTitle = (sub, section) =>
    sub.title !== section.title &&
    (section.subsections || []).length > 1 &&
    !rendersOwnTitle(sub);

// Declared after the edition-1 renderers it references; function declarations
// and const arrow functions are all hoisted into module scope before render.
const SubsectionRenderer = ({ data, submissions = [] }) => {
    switch (data.type) {
        case 'content-image': return <ContentImage data={data} />;
        case 'two-column':    return <TwoColumn data={data} />;
        case 'bullets-bold':  return <BulletsBold data={data} />;
        case 'dataset-card':  return <DatasetCard data={data} />;
        case 'timeline':      return <Timeline data={data} />;
        case 'prizes':        return <Prizes data={data} />;
        case 'judging':       return <Judging data={data} />;
        case 'steps':         return <Steps data={data} />;
        case 'content':       return <Content data={data} />;
        case 'faq-segment':   return <FAQSegment data={data} />;

        // Edition 1 only
        case 'tracks':        return <Tracks data={data} />;
        case 'results-intro': return <ResultsIntro data={data} />;
        case 'winner':        return <Winner data={data} />;
        case 'contact':       return <ContactSection data={data} />;
        case 'hubspot-form':  return <HubSpotForm data={data} />;
        case 'finalists-from-csv':
            return <SubmissionGrid submissions={submissions.filter(s => s.finalist)} badge="finalist" />;
        case 'honourable-mentions-from-csv':
            return <SubmissionGrid submissions={submissions.filter(s => s.honourableMention)} badge="mention" />;

        default:              return null;
    }
};


// ── Edition 1 renderers ───────────────────────────────────────────────────────

const Tracks = ({ data }) => (
    <div className="acp-tracks">
        {data.intro && <p className="acp-tracks__intro">{data.intro}</p>}
        <div className="acp-tracks__grid">
            {(data.items || []).map((track, i) => (
                <article key={i} className="acp-card acp-card--track">
                    <header className="acp-card__head">
                        <span className="acp-card__num">{track.number}</span>
                        <h4 className="acp-card__title">{track.title}</h4>
                    </header>
                    <div className="acp-card__body">
                        {track.goal && (
                            <p className="acp-card__goal"><strong>Goal:</strong> {track.goal}</p>
                        )}
                        <Bullets items={track.objectives} />
                    </div>
                </article>
            ))}
        </div>
    </div>
);

const ResultsIntro = ({ data }) => (
    <WithFigure data={data}>
        <Paragraphs paragraphs={data.paragraphs} />
    </WithFigure>
);

const Winner = ({ data }) => (
    <div className="acp-winner">
        {data.image && (
            <div className="acp-winner__media">
                <img src={data.image} alt={data.name || 'Winner'} loading="lazy" />
            </div>
        )}
        <div>
            {data.track && <span className="acp-winner__track">{data.track}</span>}
            <h4 className="acp-winner__name">{data.name}</h4>
            <p className="acp-winner__project">
                <a href={data.github} target="_blank" rel="noopener noreferrer">{data.project}</a>
            </p>
        </div>
    </div>
);

const YouTubeIcon = () => (
    <svg viewBox="0 0 24 24" fill="currentColor" aria-hidden="true">
        <path d="M23.495 6.205a3.007 3.007 0 0 0-2.088-2.088c-1.87-.501-9.396-.501-9.396-.501s-7.507-.01-9.396.501A3.007 3.007 0 0 0 .527 6.205a31.247 31.247 0 0 0-.522 5.805 31.247 31.247 0 0 0 .522 5.783 3.007 3.007 0 0 0 2.088 2.088c1.868.502 9.396.502 9.396.502s7.506 0 9.396-.502a3.007 3.007 0 0 0 2.088-2.088 31.247 31.247 0 0 0 .5-5.783 31.247 31.247 0 0 0-.5-5.805zM9.609 15.601V8.408l6.264 3.602z" />
    </svg>
);

const SubmissionCard = ({ s, badge }) => {
    const video = findVideo(s.name);
    return (
        <article className="acp-submission">
            <div className="acp-submission__badges">
                <span className="acp-submission__track">{s.track}</span>
                {badge && (
                    <span className="acp-submission__badge">
                        {badge === 'finalist' ? 'Finalist' : 'Honourable Mention'}
                    </span>
                )}
            </div>
            <h4 className="acp-submission__name">{capitalizeName(s.name)}</h4>
            <p className="acp-submission__title">
                <a href={s.github} target="_blank" rel="noopener noreferrer">{s.title}</a>
            </p>
            {video && (
                <a className="acp-submission__video" href={youtubeUrl(video.youtubeId)}
                    target="_blank" rel="noopener noreferrer">
                    <YouTubeIcon />
                    Watch presentation
                </a>
            )}
        </article>
    );
};

const SubmissionGrid = ({ submissions, badge }) => {
    if (!submissions.length) return <p>Loading…</p>;
    return (
        <div className="acp-submissions">
            {submissions.map(s => <SubmissionCard key={s.id} s={s} badge={badge} />)}
        </div>
    );
};

// ── Registration form ─────────────────────────────────────────────────────────

const HUBSPOT_EMBED_SRC = '//js-eu1.hsforms.net/forms/embed/v2.js';

// Load the embed script once per page, no matter how many forms mount or how
// often StrictMode re-runs effects — a second create() into the same
// container yields two embeds resizing each other in a ResizeObserver loop.
let hubspotScriptPromise = null;
const loadHubSpotScript = () => {
    if (window.hbspt) return Promise.resolve();
    if (!hubspotScriptPromise) {
        hubspotScriptPromise = new Promise((resolve, reject) => {
            const script = document.createElement('script');
            script.src = HUBSPOT_EMBED_SRC;
            script.charset = 'utf-8';
            script.type = 'text/javascript';
            script.async = true;
            script.onload = resolve;
            script.onerror = reject;
            document.body.appendChild(script);
        });
    }
    return hubspotScriptPromise;
};

const HubSpotForm = ({ data }) => {
    const containerId = `hubspot-form-${data.formId}`;
    const containerRef = useRef(null);

    useEffect(() => {
        let cancelled = false;
        loadHubSpotScript()
            .then(() => {
                if (cancelled || !containerRef.current) return;
                containerRef.current.innerHTML = '';
                window.hbspt.forms.create({
                    portalId: data.portalId,
                    formId: data.formId,
                    region: data.region,
                    target: `#${containerId}`,
                });
            })
            .catch(() => {});
        return () => {
            cancelled = true;
        };
    }, [containerId, data.portalId, data.formId, data.region]);

    return <div id={containerId} ref={containerRef} className="acp-hubspot-form" />;
};

const ContactSection = ({ data }) => (
    <div className="acp-contact">
        {(data.items || []).map((item, i) => (
            <div key={i} className="acp-contact__item">
                <h4 className="acp-contact__title">{item.title}</h4>
                <p>{item.body}</p>
                {item.links && (
                    <div className="acp-contact__links">
                        {item.links.map((l, j) => (
                            <a key={j} className="acp-contact__link" href={l.url}
                                target="_blank" rel="noopener noreferrer">{l.label}</a>
                        ))}
                    </div>
                )}
            </div>
        ))}
    </div>
);

// ── Page ──────────────────────────────────────────────────────────────────────

const Sidebar = ({ sections, activeIdx, slug, onSectionClick }) => (
    <aside className="acp-sidebar">
        <nav className="acp-sidebar__nav">
            {sections.map((s, i) => (
                <Link
                    key={s.id}
                    to={`/analytics-cup/editions/${slug}/${s.id}`}
                    className={`acp-sidebar__link${i === activeIdx ? ' is-active' : ''}`}
                    onClick={() => onSectionClick(i)}
                >
                    <span className="acp-sidebar__idx">{String(i + 1).padStart(2, '0')}</span>
                    {s.navTitle || s.title}
                </Link>
            ))}
        </nav>
    </aside>
);

const Hero = ({ edition }) => {
    if (!edition.heroImage) return null;
    return (
        <div className="acp-hero-wrap">
            <div className="container">
                <div
                    className="acp-hero"
                    style={{
                        '--hero-img': `url('${edition.heroImage}')`,
                        '--hero-img-mobile': `url('${edition.heroImageMobile || edition.heroImage}')`,
                        '--hero-ratio': edition.heroRatio || '3744 / 1832',
                        '--hero-ratio-mobile': edition.heroRatioMobile || edition.heroRatio || '723 / 488',
                    }}
                >
                    {edition.heroCta && (
                        <div className="acp-hero__cta-wrap">
                            {/* The CTA deep-links to the register section, so it
                                routes client-side rather than reloading. */}
                            {edition.heroCta.url.startsWith('/') ? (
                                <Link to={edition.heroCta.url} className="acp-hero__cta">
                                    {edition.heroCta.label}
                                </Link>
                            ) : (
                                <a href={edition.heroCta.url} className="acp-hero__cta">
                                    {edition.heroCta.label}
                                </a>
                            )}
                        </div>
                    )}
                </div>
            </div>
        </div>
    );
};

// ── Scroll spy ────────────────────────────────────────────────────────────────

// Fraction of the viewport height used as the "you are here" line.
export const ACTIVE_LINE = 0.3;

// The active section is the last one whose top has crossed the line. Pure and
// stateless: the same scroll position always yields the same index, in either
// direction. `tops` are viewport-relative offsets in section order.
export const activeSectionIndex = (tops, line, atBottom = false) => {
    if (!tops.length) return 0;
    if (atBottom) return tops.length - 1;

    let idx = 0;
    tops.forEach((top, i) => {
        if (top <= line) idx = i;
    });
    return idx;
};

export const EditionPage = () => {
    const { slug, sectionId } = useParams();
    const navigate = useNavigate();
    const edition = EDITIONS[slug];
    const submissions = useSubmissions(edition?.submissionsFile);

    const [activeIdx, setActiveIdx] = useState(0);
    // Memoised so the scroll-spy effect below doesn't re-bind its listeners on
    // every render — `|| []` would hand it a fresh array each time.
    const sections = useMemo(() => edition?.sections || [], [edition]);

    // Section URLs are shared (emails, sitemap), so clicks and deep links keep
    // the address bar naming a section. The URL deliberately does NOT follow
    // free scrolling: renaming it mid-navigation cancels the click's smooth
    // scroll. 'click' scrolls smoothly; a deep link or back/forward
    // ('external') jumps instantly.
    const urlSourceRef = useRef('external');

    // Recomputed from scratch on every scroll rather than accumulated from
    // events, so the answer depends only on where the page is.
    useEffect(() => {
        if (!sections.length) return undefined;
        const ids = sections.map(s => `acp-${s.id}`);
        let frame = null;

        const sync = () => {
            frame = null;
            const tops = ids.map((id) => {
                const el = document.getElementById(id);
                return el ? el.getBoundingClientRect().top : Infinity;
            });
            const atBottom =
                window.innerHeight + window.scrollY >= document.body.scrollHeight - 2;
            setActiveIdx(activeSectionIndex(tops, window.innerHeight * ACTIVE_LINE, atBottom));
        };

        const onScroll = () => {
            if (frame === null) frame = requestAnimationFrame(sync);
        };

        sync();
        window.addEventListener('scroll', onScroll, { passive: true });
        window.addEventListener('resize', onScroll, { passive: true });
        return () => {
            window.removeEventListener('scroll', onScroll);
            window.removeEventListener('resize', onScroll);
            if (frame !== null) cancelAnimationFrame(frame);
        };
    }, [sections]);

    // Deep links (/editions/:slug/:sectionId) land on a section; sidebar
    // clicks arrive here too, via the navigation they trigger.
    useEffect(() => {
        const source = urlSourceRef.current;
        urlSourceRef.current = 'external';
        if (!sectionId) return;
        document.getElementById(`acp-${sectionId}`)
            ?.scrollIntoView({ behavior: source === 'click' ? 'smooth' : 'instant', block: 'start' });
    }, [sectionId, edition]);

    if (!edition) return <Navigate to="/analytics-cup" replace />;

    // Sidebar items are real <Link>s; this runs alongside their navigation to
    // mark the URL change as a click (smooth scroll) and to handle re-clicking
    // the section already in the URL, where navigation is a no-op.
    const handleSectionClick = (idx) => {
        const id = sections[idx].id;
        setActiveIdx(idx);
        if (id === sectionId) {
            document.getElementById(`acp-${id}`)
                ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
            return;
        }
        urlSourceRef.current = 'click';
    };

    // The mobile <select> has no link semantics, so it navigates itself.
    const handleNavigate = (idx) => {
        handleSectionClick(idx);
        const id = sections[idx].id;
        if (id !== sectionId) navigate(`/analytics-cup/editions/${slug}/${id}`);
    };

    const active = sections[activeIdx];
    const pageTitle = active
        ? `${active.navTitle || active.title} — ${edition.name} | PySport`
        : `${edition.name} | PySport`;

    return (
        <div className="analytics-cup analytics-cup--edition">
            <Helmet><title>{pageTitle}</title></Helmet>

            <AnalyticsCupNav />
            <Hero edition={edition} />

            <div className="acp-mobile-nav">
                <select
                    value={activeIdx}
                    onChange={e => handleNavigate(Number(e.target.value))}
                    aria-label="Navigate sections"
                >
                    {sections.map((s, i) => (
                        <option key={s.id} value={i}>{s.navTitle || s.title}</option>
                    ))}
                </select>
            </div>

            <div className="container">
                <div className="acp-layout">
                    <Sidebar sections={sections} activeIdx={activeIdx} slug={slug} onSectionClick={handleSectionClick} />

                    <div className="acp-layout__content">
                        {sections.map((section, sIdx) => (
                            <section key={section.id} id={`acp-${section.id}`} className="acp-section">
                                <header className="acp-section__header">
                                    <span className="acp-section__num">{String(sIdx + 1).padStart(2, '0')}</span>
                                    <h2 className="acp-section__title">{section.title}</h2>
                                </header>

                                <div className={isCardGrid(section) ? 'acp-subsection-grid' : undefined}>
                                    {(section.subsections || []).map((sub) => (
                                        <div key={sub.id} id={`acp-${sub.id}`} className="acp-subsection">
                                            {showSubTitle(sub, section) && (
                                                <h3 className="acp-subsection__title">{sub.title}</h3>
                                            )}
                                            <SubsectionRenderer data={sub} submissions={submissions} />
                                        </div>
                                    ))}
                                </div>
                            </section>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
};
